import { EditIcon } from "@minket/brand/icons/edit";
import { OrganizationIcon } from "@minket/brand/icons/organization";
import { Button } from "@minket/ui/components/button";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { lazy, Suspense, useState } from "react";
import { CloudflareMark } from "@/components/brands/cloudflare";
import { BrandName, markSize } from "@/components/brands/named";
import { plural, stamp } from "@/components/ops/format";
import { When } from "@/components/ops/when";
import { UnavailableAction } from "@/components/page/action";
import { DataList } from "@/components/page/data-list";
import { GhostSections } from "@/components/page/ghost";
import { SubPage } from "@/components/page/page";
import { OrgScope } from "@/components/page/scope";
import { Section, Sheet } from "@/components/page/section";
import { Serial } from "@/components/page/serial";
import { QueryView } from "@/components/page/states";
import type { OrganizationBody } from "@/components/tenancy/settings.ondemand";
import { endpoints, type ResponseOf } from "@/lib/endpoints";
import { useCan } from "@/lib/permissions";
import { sessionKey } from "@/lib/queries";
import { apiKey, apiQuery, apiRequest } from "@/lib/query";
import { displayName } from "@/lib/session";
import { notify } from "@/lib/toast";
import { useWorkspace } from "@/lib/workspace";

export const Route = createFileRoute("/settings/organization")({
  component: OrganizationSettings,
});

type Organization = ResponseOf<typeof endpoints.organization>;

const loadDialogs = () => import("@/components/tenancy/settings.ondemand");
const loadSign = () => import("@/components/tenancy/countersign.ondemand");
const OrganizationDialog = lazy(() =>
  loadDialogs().then((m) => ({ default: m.OrganizationDialog })),
);
const Sign = lazy(() => loadSign().then((m) => ({ default: m.Sign })));

const sections = [
  {
    title: "Details",
    description: "Its name, slug and id.",
    fields: ["Name", "Slug", "Created", "Id"],
  },
  {
    title: "Regions",
    description: "Where its mail leaves from.",
    fields: ["Primary", "Secondary"],
  },
  { title: "Cloudflare", description: "The zones Minket may write DNS in.", fields: ["Zones"] },
  { title: "Delete organization", description: "Owners only, once it has no projects." },
];
const ghost = () => <GhostSections sections={sections} headingLevel={3} />;

/** The organization as a sheet (direction §7, Settings): ruled sections, each with its label. */
function OrganizationSettings() {
  const { organization } = useWorkspace();
  const orgId = organization?.organization_id ?? "";
  const isOwner = useCan("org.manage");
  const queryClient = useQueryClient();
  const query = useQuery({ ...apiQuery(endpoints.organization, { orgId }), enabled: orgId !== "" });
  const [editing, setEditing] = useState(false);
  const save = useMutation({
    mutationFn: (body: OrganizationBody) =>
      apiRequest(endpoints.updateOrganization, { orgId }, { body }),
    onSuccess: (updated) => {
      queryClient.setQueryData(apiQuery(endpoints.organization, { orgId }).queryKey, updated);
      void queryClient.invalidateQueries({ queryKey: sessionKey });
      notify("success", "Organization saved.");
      setEditing(false);
    },
  });
  const action =
    isOwner && query.data ? (
      <Button
        onClick={() => {
          save.reset();
          setEditing(true);
        }}
        onPointerEnter={() => void loadDialogs()}
        onFocus={() => void loadDialogs()}
      >
        <EditIcon size={20} aria-hidden />
        Edit organization
      </Button>
    ) : (
      <UnavailableAction
        label="Edit organization"
        icon={EditIcon}
        reason={isOwner ? "Loading the organization" : "Owners edit the organization"}
      />
    );
  return (
    <SubPage title="Organization" action={action}>
      <OrgScope
        will="Its name, regions and Cloudflare zones appear here."
        icon={<OrganizationIcon />}
        preview={ghost}
        headingLevel={3}
      >
        {() => (
          <QueryView query={query} noun="the organization" preview={ghost} headingLevel={3}>
            {(org) => <Details org={org} isOwner={isOwner} />}
          </QueryView>
        )}
      </OrgScope>
      {editing && query.data ? (
        <Suspense fallback={null}>
          <OrganizationDialog
            organization={query.data}
            save={save}
            onClose={() => setEditing(false)}
          />
        </Suspense>
      ) : null}
    </SubPage>
  );
}

const link =
  "rounded-sm text-sm font-medium text-primary underline decoration-1 underline-offset-4 hover:decoration-2 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none";

function Details({ org, isOwner }: { org: Organization; isOwner: boolean }) {
  const { projects, user } = useWorkspace();
  const queryClient = useQueryClient();
  const [deleting, setDeleting] = useState(false);
  const remove = useMutation({
    mutationFn: () => apiRequest(endpoints.deleteOrganization, { orgId: org.id }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: sessionKey });
      queryClient.removeQueries({ queryKey: apiKey(endpoints.organization) });
      // Its audit log goes with it, so there is no entry to link to.
      notify("success", `${org.name} deleted.`);
      setDeleting(false);
    },
  });
  const projectCount = projects?.data?.data.length ?? null;
  return (
    <Sheet>
      <Section headingLevel={3} title="Details">
        <DataList
          items={[
            { label: "Name", value: org.name },
            { label: "Slug", value: org.slug, data: true },
            { label: "Created, UTC", value: <When value={org.created_at} />, nowrap: true },
            { label: "Id", value: <Serial id={org.id} label="organization id" />, nowrap: true },
          ]}
        />
      </Section>
      <Section
        headingLevel={3}
        title="Regions"
        description="Mail leaves through the primary region. The secondary one takes over only when SES refuses it there."
        action={
          <Link to="/status" className={link}>
            See failover on Status
          </Link>
        }
      >
        <DataList
          items={[
            { label: "Primary", value: org.primary_region, data: true, nowrap: true },
            {
              label: "Secondary",
              value: org.secondary_region ?? "None",
              data: org.secondary_region !== null,
              nowrap: true,
            },
          ]}
        />
      </Section>
      <Section
        headingLevel={3}
        title={
          <BrandName mark={<CloudflareMark className={markSize.cloudflare} />}>
            Cloudflare
          </BrandName>
        }
        action={
          <Link to="/settings/cloudflare" className={link}>
            Cloudflare settings
          </Link>
        }
      >
        {org.cloudflare.connected ? (
          <DataList
            items={[
              {
                label: "Zones",
                value:
                  org.cloudflare.zones.length === 0 ? "None yet" : org.cloudflare.zones.join(", "),
                data: org.cloudflare.zones.length > 0,
              },
            ]}
          />
        ) : (
          <p className="text-sm text-muted-foreground">
            Not connected. Each domain's records are shown to copy instead.
          </p>
        )}
      </Section>
      <Section
        headingLevel={3}
        title="Delete organization"
        description="Its members, settings and audit log go with it."
        action={
          isOwner && projectCount === 0 ? (
            <Button
              variant="destructive"
              onPointerEnter={() => void loadSign()}
              onClick={() => {
                remove.reset();
                setDeleting(true);
              }}
            >
              Delete organization
            </Button>
          ) : (
            <UnavailableAction
              label="Delete organization"
              variant="outline"
              reason={
                !isOwner
                  ? "Owners delete organizations"
                  : projectCount === null
                    ? "Loading its projects"
                    : `Delete its ${plural(projectCount, "project")} first`
              }
            />
          )
        }
      />
      {deleting ? (
        <Suspense fallback={null}>
          <Sign
            title={`Delete ${org.name}`}
            description="Its members, settings and audit log are deleted with it. This cannot be undone."
            plan={{
              label: "What deleting removes",
              columns: ["Action", "What", "Since"],
              steps: [
                {
                  key: org.id,
                  verdict: "Delete",
                  subject: org.name,
                  detail: org.slug,
                  reason: `${stamp(org.created_at)} UTC`,
                },
              ],
            }}
            act="Delete organization"
            tone="destructive"
            pending={remove.isPending}
            error={remove.error}
            signer={displayName(user)}
            onConfirm={() => remove.mutate()}
            onClose={() => setDeleting(false)}
          />
        </Suspense>
      ) : null}
    </Sheet>
  );
}
